import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DateTimePicker } from './DateTimePicker';
import { TemplateSelector } from './TemplateSelector';
import { PositionSelector } from './PositionSelector';
import { CandidateFields, type CandidateData } from './CandidateFields';
import type { SessionTemplate, Position } from '@arrowise/shared';

export interface SessionFormData {
    dueAt: Date | null;
    candidate: CandidateData;
    selectedTemplate: SessionTemplate | null;
    selectedPosition: Position | null;
}

export interface SessionFormProps {
    data: SessionFormData;
    onDataChange: (data: SessionFormData) => void;
    onSubmit: (e: React.FormEvent) => void;
    teamId?: string | null;
    candidateEditable?: boolean;
    useParticipantsEditor?: boolean;
    submitLabel: string;
    submitDisabled?: boolean;
    loading?: boolean;
    templateDialogOpen: boolean;
    onTemplateDialogOpenChange: (open: boolean) => void;
    showDueDate?: boolean;
    dueDateRequired?: boolean;
}

export function SessionForm({
    data,
    onDataChange,
    onSubmit,
    teamId = null,
    candidateEditable = true,
    useParticipantsEditor = false,
    submitLabel,
    submitDisabled = false,
    loading = false,
    templateDialogOpen,
    onTemplateDialogOpenChange,
    showDueDate = true,
    dueDateRequired = false
}: SessionFormProps) {
    const updateData = (updates: Partial<SessionFormData>) => {
        onDataChange({ ...data, ...updates });
    };

    return (
        <form className="space-y-6" onSubmit={onSubmit}>
            <PositionSelector
                selectedPosition={data.selectedPosition}
                onPositionSelect={(position) => updateData({ selectedPosition: position })}
                teamId={teamId}
                required={true}
            />

            <TemplateSelector
                selectedTemplate={data.selectedTemplate}
                onTemplateSelect={(template) => updateData({ selectedTemplate: template })}
                teamId={teamId}
                dialogOpen={templateDialogOpen}
                onDialogOpenChange={onTemplateDialogOpenChange}
                isManage={false}
            />

            <CandidateFields
                candidate={data.candidate}
                onCandidateChange={(candidate) => updateData({ candidate })}
                editable={candidateEditable}
                useParticipantsEditor={useParticipantsEditor}
            />

            {showDueDate && (
                <div className="flex flex-col gap-2">
                    <Label>Due date</Label>
                    <DateTimePicker
                        value={data.dueAt}
                        onChange={(dueAt) => updateData({ dueAt })}
                        required={dueDateRequired}
                    />
                </div>
            )}

            <div className="flex items-center gap-2">
                <Button
                    type="submit"
                    disabled={submitDisabled || loading}
                >
                    {submitLabel}
                </Button>
            </div>
        </form>
    );
}
